import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import styled from 'styled-components'
import axios from 'axios'
import { toast } from 'sonner'
import { contactSchema, type ContactFormInput } from '@src/schemas/contactSchema'
import { contactAPI } from '@src/api'
import { useTurnstile } from '@src/hooks/useTurnstile'
import { useLocalStorage } from '@src/hooks/useLocalStorage'
import StyledAlert from '@src/components/StyledAlert'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import ContactHero from '@design-components/ContactPageHeader'
import {
  FormGroup, FormLabel, FormInput, FormTextarea, FormHint,
  FieldError, CharCount, PrimaryButton, GrayButton, colors,
} from '@src/theme/sharedComponents'

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 200px);
`

const FormSection = styled.div`
  padding: 3rem 2rem;
  background: ${colors.backgroundAlt};
`

const ContentWrapper = styled.div`
  max-width: 760px;
  margin: 0 auto;
`

const FormCard = styled.div`
  background: white;
  border-radius: var(--radius);
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);

  @media (max-width: 576px) { padding: 1.5rem; }
`

const SuccessContainer = styled.div`
  text-align: center;
  padding: 2rem 0;
`

const SuccessIcon = styled.div`
  font-size: 3rem;
  color: ${colors.success};
  margin-bottom: 1rem;
`

const SuccessTitle = styled.h2`
  color: ${colors.textLightMode};
  font-weight: 700;
  margin-bottom: 0.75rem;
`

const SuccessText = styled.p`
  color: ${colors.textLightModeMuted};
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
  @media (max-width: 480px) { flex-direction: column; }
`

const TurnstileContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.25rem;
  min-height: 65px;
`

const ContactPage: React.FC = () => {
  const { data: s } = usePublicSettings()
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [apiError, setApiError] = useState('')
  const [rateLimitedUntil, setRateLimitedUntil, removeRateLimit] = useLocalStorage<number | null>('contact_rate_limit_until', null)
  const [countdown, setCountdown] = useState<number>(0)
  const { turnstileRef, getToken, reset: resetTurnstile } = useTurnstile(true)

  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<ContactFormInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: '', email: '', company: '', phone: '', partInquiry: '', message: '' },
  })

  useEffect(() => {
    if (!rateLimitedUntil) { setCountdown(0); return }
    const update = () => {
      const remaining = Math.ceil((rateLimitedUntil - Date.now()) / 1000)
      if (remaining <= 0) { setCountdown(0); removeRateLimit(); setApiError('') }
      else setCountdown(remaining)
    }
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [rateLimitedUntil, removeRateLimit])

  const formatCountdown = (sec: number) => {
    const m = Math.floor(sec / 60)
    return m > 0 ? `${m}m ${sec % 60}s` : `${sec}s`
  }

  const onSubmit = async (data: ContactFormInput) => {
    setApiError('')
    const turnstileToken = getToken()
    if (!turnstileToken) { setApiError('Please complete the security verification.'); return }
    try {
      await contactAPI.submit({ name: data.name, email: data.email, message: data.message, company: data.company, phone: data.phone, partInquiry: data.partInquiry, turnstileToken })
      setFormSubmitted(true)
      reset()
      resetTurnstile()
      toast.success('Message sent!')
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after']
        const waitSeconds = retryAfter ? parseInt(retryAfter, 10) : 3600
        setRateLimitedUntil(Date.now() + waitSeconds * 1000)
        setApiError(`You've reached the submission limit. Please try again in ${formatCountdown(waitSeconds)}.`)
      } else if (axios.isAxiosError(error)) {
        setApiError(error.response?.data?.error || 'Sorry, there was an error. Please try again.')
      } else {
        setApiError('Sorry, there was an error. Please try again or contact us directly.')
      }
    }
  }

  const messageLength = watch('message')?.length || 0

  return (
    <PageContainer>
      <ContactHero />

      <FormSection>
        <ContentWrapper>
          <FormCard>
            {formSubmitted ? (
              <SuccessContainer>
                <SuccessIcon><i className="bi bi-check-circle-fill" /></SuccessIcon>
                <SuccessTitle>Message Sent!</SuccessTitle>
                <SuccessText>We've received your message and will get back to you within 24 hours.</SuccessText>
                <ButtonGroup>
                  <GrayButton onClick={() => { setFormSubmitted(false); setApiError(''); resetTurnstile() }}>
                    <i className="bi bi-plus-circle me-2" />Send Another
                  </GrayButton>
                  {s?.contact_phone && (
                    <PrimaryButton as="a" href={`tel:${s.contact_phone.replace(/\D/g, '')}`}>
                      <i className="bi bi-telephone me-2" />Call Us Now
                    </PrimaryButton>
                  )}
                </ButtonGroup>
              </SuccessContainer>
            ) : (
              <>
                {apiError && <StyledAlert variant="danger" dismissible onClose={() => setApiError('')} className="mb-3">{apiError}</StyledAlert>}
                {countdown > 0 && (
                  <StyledAlert variant="warning" className="mb-3">
                    <strong><i className="bi bi-hourglass-split me-2" />Rate Limited</strong>{' '}
                    Please try again in {formatCountdown(countdown)}.
                  </StyledAlert>
                )}
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row" style={{ marginBottom: '1.25rem' }}>
                    <div className="col-md-6">
                      <FormGroup>
                        <FormLabel>Full Name <span className="required">*</span></FormLabel>
                        <FormInput type="text" placeholder="Your full name" maxLength={100} {...register('name')} />
                        {errors.name && <FieldError>{errors.name.message}</FieldError>}
                      </FormGroup>
                    </div>
                    <div className="col-md-6">
                      <FormGroup>
                        <FormLabel>Email Address <span className="required">*</span></FormLabel>
                        <FormInput type="email" placeholder="you@email.com" maxLength={120} {...register('email')} />
                        {errors.email && <FieldError>{errors.email.message}</FieldError>}
                      </FormGroup>
                    </div>
                  </div>
                  <div className="row" style={{ marginBottom: '1.25rem' }}>
                    <div className="col-md-6">
                      <FormGroup>
                        <FormLabel>Company <span style={{ color: colors.textLightModeMuted }}>(optional)</span></FormLabel>
                        <FormInput type="text" placeholder="Your company name" maxLength={100} {...register('company')} />
                      </FormGroup>
                    </div>
                    <div className="col-md-6">
                      <FormGroup>
                        <FormLabel>Phone <span style={{ color: colors.textLightModeMuted }}>(optional)</span></FormLabel>
                        <FormInput type="tel" placeholder="(555) 123-4567" maxLength={20} {...register('phone')} />
                      </FormGroup>
                    </div>
                  </div>
                  <FormGroup>
                    <FormLabel>Part Inquiry <span style={{ color: colors.textLightModeMuted }}>(optional)</span></FormLabel>
                    <FormInput type="text" placeholder={s?.contact_part_inquiry_placeholder || 'e.g. Product name, SKU, Part #12345'} maxLength={200} {...register('partInquiry')} />
                    <FormHint>Looking for a specific part? Let us know and we'll check availability.</FormHint>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Message <span className="required">*</span></FormLabel>
                    <FormTextarea placeholder="How can we help you today?" maxLength={2000} rows={5} {...register('message')} />
                    <div className="d-flex justify-content-between mt-1">
                      <span>{errors.message && <FieldError style={{ marginTop: 0 }}>{errors.message.message}</FieldError>}</span>
                      <CharCount $warning={messageLength > 1600} $danger={messageLength > 1800}>{messageLength}/2000</CharCount>
                    </div>
                  </FormGroup>
                  <TurnstileContainer><div ref={turnstileRef} /></TurnstileContainer>
                  <div className="d-grid gap-2">
                    <PrimaryButton type="submit" disabled={isSubmitting || countdown > 0}>
                      {isSubmitting ? <><span className="spinner-border spinner-border-sm me-2" />Sending...</> : countdown > 0 ? <><i className="bi bi-hourglass-split me-2" />Try again in {formatCountdown(countdown)}</> : <><i className="bi bi-send me-2" />Send Message</>}
                    </PrimaryButton>
                    <GrayButton type="button" onClick={() => { reset(); setApiError('') }} disabled={isSubmitting}>
                      <i className="bi bi-arrow-counterclockwise me-2" />Clear Form
                    </GrayButton>
                  </div>
                </form>
              </>
            )}
          </FormCard>
        </ContentWrapper>
      </FormSection>
    </PageContainer>
  )
}

export default ContactPage
